import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import thunkActions from '../actions/thunkActions';
import errorHandler from '../../../common/service/errorHandler';
import initialState from '../initialState';
import { IPriceDisplay } from '../../../common/model/IPlatformConfig';
import dealPriceAccessor from '../../../common/service/dealPriceAccessor';
import { IPrice, IPrices } from '../../../../../common/model/IPrice';
import ICarConfigOption from '../../../../../common/model/ICarConfigOption';
import { OptionType } from '../../../common/enum/OptionType';
import carPriceCalculator from '../../../common/service/carPriceCalculator';
import currentDealServices from '../actions/currentDealServices';
import financeQuoteTransformer from '../../../common/service/financeQuoteTransformer';
import { IPlainBaseFinanceQuote, IPlainFinanceQuote } from '../../../common/model/IFinanceQuote';
import { IDealThunk } from '../../model/IThunkData';

export type IDealState = typeof initialState.currentDealInitialState;

const currentDealReducer = createSlice({
  name: 'CurrentDealReducer',
  initialState: initialState.currentDealInitialState,
  reducers: {
    setFinanceQuote: (state, { payload: plainFinanceQuote }: PayloadAction<IPlainFinanceQuote>) => {
      state.financeQuote = financeQuoteTransformer.from(plainFinanceQuote);
      currentDealReducer.caseReducers.setSecondaryFinanceQuote(state, {
        payload: plainFinanceQuote,
        type: 'setSecondaryFinanceQuote',
      });
    },
    setSecondaryFinanceQuote: (state, { payload: plainFinanceQuote }: PayloadAction<IPlainBaseFinanceQuote>) => {
      state.secondaryFinanceQuote = financeQuoteTransformer.from(plainFinanceQuote);
    },
    setTotalAmountPayable: (
      state,
      {
        payload,
      }: PayloadAction<{
        carPrices: IPrices;
        priceDisplay: IPriceDisplay;
        financeDeposit: number;
        options: ICarConfigOption[];
      }>,
    ) => {
      const { carPrices, priceDisplay, financeDeposit, options } = payload;
      const primaryTotalPrice = dealPriceAccessor.getTotalPrice(carPrices, priceDisplay);
      const getServicePlan: () => ICarConfigOption | null = () =>
        options.find(
          (option: ICarConfigOption) =>
            option.category.category &&
            option.category.category.toLowerCase() === OptionType.SERVICE_PLAN.toLowerCase(),
        );
      const hasServicePlan = Boolean(getServicePlan());
      const { basePrice: servicePlanBasePrice } = hasServicePlan
        ? dealPriceAccessor.getFittedPrice(getServicePlan().prices)
        : { basePrice: 0 };

      const totalPayable: IPrice = carPriceCalculator.getTotalPayable(
        primaryTotalPrice,
        servicePlanBasePrice,
        financeDeposit,
      );

      state.prices.totalAmountPayable = totalPayable;
    },
    setPrimaryTotalPrice: (state, { payload }: PayloadAction<{ carPrices: IPrices; priceDisplay: IPriceDisplay }>) => {
      const { carPrices, priceDisplay } = payload;
      state.prices.primaryTotalPrice = dealPriceAccessor.getTotalPrice(carPrices, priceDisplay);
    },
    setSecondaryTotalPrice: (
      state,
      { payload }: PayloadAction<{ carPrices: IPrices; priceDisplay: IPriceDisplay }>,
    ) => {
      const { carPrices, priceDisplay } = payload;
      state.prices.secondaryTotalPrice = dealPriceAccessor.getSecondaryTotalPrice(carPrices, priceDisplay);
    },
    setPrimaryPrice: (state, { payload }: PayloadAction<{ carPrices: IPrices; priceDisplay: IPriceDisplay }>) => {
      const { carPrices, priceDisplay } = payload;
      state.prices.primaryPrice = dealPriceAccessor.getPrimaryPrice(carPrices, priceDisplay);
    },
    setSecondaryPrice: (state, { payload }: PayloadAction<{ carPrices: IPrices; priceDisplay: IPriceDisplay }>) => {
      const { carPrices, priceDisplay } = payload;
      state.prices.secondaryPrice = dealPriceAccessor.getSecondaryPrice(carPrices, priceDisplay);
    },
    setDeposit: (state, { payload: deposit }: PayloadAction<number>) => {
      state.prices.deposit = deposit;
    },
    setError: (state, { payload: error }: PayloadAction<any>) => {
      state.error = error;
    },
  },
  extraReducers: builder => {
    const onDealPending = (state: IDealState): IDealState => {
      return {
        ...state,
        loading: true,
        loadingCount: state.loadingCount + 1,
      };
    };

    const onDealRejected = (state: IDealState, action: { error: any }): IDealState => {
      if (errorHandler.handleError(action.error) === false) {
        return {
          ...initialState.currentDealInitialState,
          loading: false,
          error: action.error,
        };
      }

      return {
        ...state,
        loading: state.loadingCount - 1 !== 0,
        loadingCount: state.loadingCount - 1,
      };
    };

    const onDealFulfilled = (state: IDealState, action: PayloadAction<IDealThunk>) => {
      currentDealServices.setCarPrices(state, action);
      currentDealServices.setOptions(state, action);
      currentDealServices.setServicePlan(state, action);
      currentDealServices.setFinanceQuotes(state, action);

      state.prices.isMultiCurrency = dealPriceAccessor.isMultiCurrency(action.payload.priceDisplay);
      state.currentDeal = action.payload.deal;
      state.loading = state.loadingCount - 1 !== 0;
      state.loadingCount = state.loadingCount - 1;
      state.error = null;
    };

    builder.addCase(thunkActions.approveCashDeal.pending, onDealPending);
    builder.addCase(thunkActions.approveCashDeal.fulfilled, (state: IDealState, action: PayloadAction<IDealThunk>) => {
      currentDealServices.setCarPrices(state, action);
      currentDealServices.setOptions(state, action);
      currentDealServices.setServicePlan(state, action);
      currentDealServices.setFinanceQuotes(state, action);
      state.currentDeal = action.payload.deal;
      state.loading = state.loadingCount - 1 !== 0;
      state.loadingCount = state.loadingCount - 1;
      state.error = null;
    });
    builder.addCase(thunkActions.approveCashDeal.rejected, onDealRejected);

    builder.addCase(thunkActions.initBasket.pending, onDealPending);
    builder.addCase(thunkActions.initBasket.fulfilled, onDealFulfilled);
    builder.addCase(thunkActions.initBasket.rejected, onDealRejected);

    builder.addCase(thunkActions.selectStockCar.pending, onDealPending);
    builder.addCase(thunkActions.selectStockCar.fulfilled, onDealFulfilled);
    builder.addCase(thunkActions.selectStockCar.rejected, onDealRejected);

    builder.addCase(thunkActions.selectCar.pending, onDealPending);
    builder.addCase(thunkActions.selectCar.fulfilled, onDealFulfilled);
    builder.addCase(thunkActions.selectCar.rejected, onDealRejected);

    builder.addCase(thunkActions.selectInterior.pending, onDealPending);
    builder.addCase(thunkActions.selectInterior.fulfilled, onDealFulfilled);
    builder.addCase(thunkActions.selectInterior.rejected, onDealRejected);

    builder.addCase(thunkActions.selectExterior.pending, onDealPending);
    builder.addCase(thunkActions.selectExterior.fulfilled, onDealFulfilled);
    builder.addCase(thunkActions.selectExterior.rejected, onDealRejected);
  },
});

export const currentDealActions = currentDealReducer.actions;

export default currentDealReducer.reducer;
